import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { BarChart, TickY } from '@charts';
import { Box, Dropdown, DropdownItem, DropdownVariant } from '@base-app';
import { DateRangeSelection } from '@builder';
import { DateSort, ModalParticipantsProps } from './ModalParticipants.types';
import { PartnerOrganization } from '@/modules/core/types';
import { PillIds } from '../types/PillIds';
import { Serie } from '@/modules/shared/types/DynamicBarChart.types';
import { Sizes } from '@/common/types';
import { STUDY_INSIGHTS_TRANSLATION } from '@/modules/study-insights/constants';
import { StyledBaseline, StyledDescription, StyledSubPageContainer } from './ModalParticipants.styles';

import ChartContainer from '../ChartContainer/ChartContainer';
import ChartPillAndDateFilter from '../ChartPillAndDateFilter/ChartPillAndDateFilter';
import useOrganizationName from '@/modules/my-account/hooks/useOrganizationName';

const ModalParticipants: FC<ModalParticipantsProps> = ({
  chartData,
  onChangeDate,
  pillOptions,
  selectedPill,
  viewFilter,
}) => {
  const { t } = useTranslation(STUDY_INSIGHTS_TRANSLATION);
  const [data, setData] = useState<Serie[]>(chartData);
  const [selectedSort, setSelectedSort] = useState<string>(t('daily_view'));
  const [tickY, setTickY] = useState<TickY>();
  const [tooltip, setTooltip] = useState<string | undefined>();

  const organizationName = useOrganizationName();
  const isMemorialHermann =
    organizationName === PartnerOrganization.MEMORIAL_HERMANN ||
    organizationName === PartnerOrganization.HELIX_MEMORIAL_HERMANN;

  const handlePillClick = (id: string): void => {
    switch (id) {
      case PillIds.CONSENTED:
        setTooltip(t('participant_recruitment.chart_tooltip', { section: id.charAt(0).toUpperCase() + id.slice(1) }));
        setTickY({
          max: 500,
          min: 0,
          unit: { onlyOnMax: true, symbol: '+' },
        });
        break;

      case PillIds.INTERESTED:
        setTooltip(
          t('participant_recruitment.chart_tooltip', {
            section: `${
              isMemorialHermann
                ? t('participant_recruitment.available_for_consent.title')
                : id.charAt(0).toUpperCase() + id.slice(1)
            }`,
          }),
        );
        setTickY({
          max: 500,
          min: 0,
          unit: { onlyOnMax: true, symbol: '+' },
        });
        break;
      case PillIds.RATE_CONSENTED:
        setTooltip(t('participant_recruitment.expanded_chart_consented_conversion_tooltip'));
        setTickY({
          max: 100,
          min: 0,
          unit: { symbol: '%' },
        });
        break;
      case PillIds.RATE_INTERESTED:
        setTooltip(
          t(
            `participant_recruitment.${
              isMemorialHermann
                ? 'expanded_chart_available_for_consent_conversion_tooltip'
                : 'expanded_chart_interested_conversion_tooltip'
            }`,
          ),
        );
        setTickY({
          max: 100,
          min: 0,
          unit: { symbol: '%' },
        });
        break;
      default:
        setTickY(undefined);
        break;
    }
  };

  const handleSelectDateRange = (selection: DateRangeSelection): void => {
    onChangeDate?.(selection);
  };

  const options: DropdownItem[] = [
    {
      id: DateSort.DAY,
      label: t('daily_view'),
    },
    {
      id: DateSort.WEEK,
      label: t('weekly_view'),
    },
  ];

  const handlerSortedTime = (item: DropdownItem): void => {
    setSelectedSort(item.label);
    viewFilter?.(item.id as DateSort);
  };

  useEffect(() => {
    setData(chartData);
  }, [chartData]);

  return (
    <Box>
      <StyledSubPageContainer>
        <ChartPillAndDateFilter
          pillOptions={pillOptions}
          selectedPill={selectedPill}
          onChangeDate={handleSelectDateRange}
          onChangePill={handlePillClick}
        />
      </StyledSubPageContainer>
      <ChartContainer>
        <Box
          css={{
            display: 'flex',
            justifyContent: 'space-between',
            padding: '$4 $7 $8 $7',
            width: '100%',
          }}
        >
          <Box>
            <StyledBaseline />
            <StyledDescription>
              <Box>{t('participant_recruitment.weekly_consent_goals')}</Box>
              <Box css={{ paddingLeft: '$1', fontWeight: '$bold' }}>
                {t('participant_recruitment.total_participants', { total: 385 })}
              </Box>
            </StyledDescription>
          </Box>
          <Dropdown
            id="dropdown-modal-participants"
            items={options}
            onSelect={handlerSortedTime}
            size={Sizes.MEDIUM}
            title={selectedSort}
            variant={DropdownVariant.PILL}
          />
        </Box>
        <BarChart
          tickY={tickY}
          chartProps={{
            minHeight: 320,
            minWidth: 700,
          }}
          baseline={300}
          colors={{ baseline: '#7ACB71' }}
          data={data}
          tooltip={tooltip}
        />
      </ChartContainer>
    </Box>
  );
};

export default ModalParticipants;
